import { useState } from "react";
import { Link } from "react-router";
import { ChevronRight, Filter, Layers } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/shared/empty-state";
import { formatDate } from "@/lib/utils";
import {
  SEGMENT_ENTITY_LABEL,
  SEGMENT_ENTITY_TYPES,
  type SegmentEntityType,
  countConditions,
  isFilterGroup,
  type FilterTree,
} from "@colab/shared";
import type { SegmentRow } from "@/features/settings/use-segments";
import { CreateSegmentSheet } from "./create-segment-sheet";

// SPA port of /app/admin/settings/segments/_components/SegmentsTabs.tsx.
// Groups segments by entity_type (partner / deal / submission / journey /
// content_asset / mdf_request) into a Tabs component; each tab shows the
// count + the rows for that entity. admin_deal segments don't exist (only
// admin_deal views), so admin_deal is intentionally omitted from this list.

type Props = {
  segments: SegmentRow[];
  canManage: boolean;
};

export function SegmentsTabs({ segments, canManage }: Props) {
  const [active, setActive] = useState<SegmentEntityType>("partner");

  const byType: Record<SegmentEntityType, SegmentRow[]> = {
    partner: [],
    deal: [],
    submission: [],
    journey: [],
    content_asset: [],
    mdf_request: [],
  };
  for (const s of segments) {
    byType[s.entity_type]?.push(s);
  }

  return (
    <Tabs value={active} onValueChange={(v) => setActive(v as SegmentEntityType)}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <TabsList>
          {SEGMENT_ENTITY_TYPES.map((t) => (
            <TabsTrigger key={t} value={t}>
              {SEGMENT_ENTITY_LABEL[t]}
              <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                {byType[t].length}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
        {canManage && <CreateSegmentSheet defaultEntityType={active} />}
      </div>

      {SEGMENT_ENTITY_TYPES.map((t) => {
        const list = byType[t];
        return (
          <TabsContent key={t} value={t} className="mt-0">
            {list.length > 0 ? (
              <ul className="space-y-2">
                {list.map((s) => {
                  const count = isFilterGroup(s.filter)
                    ? countConditions(s.filter as FilterTree)
                    : 0;
                  return (
                    <li key={s.id}>
                      <Link
                        to={`/admin/settings/segments/${s.id}`}
                        className="group flex items-center gap-4 rounded-xl border bg-card p-4 transition-colors hover:border-accent/40 hover:bg-accent/[0.02]"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Filter className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate font-display text-sm font-semibold tracking-tight">
                            {s.name}
                          </h3>
                          {s.description && (
                            <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">
                              {s.description}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-6 text-right">
                          <Stat
                            label={count === 1 ? "Condition" : "Conditions"}
                            value={count}
                          />
                          <div className="hidden text-right sm:block">
                            <div className="text-[11px] text-muted-foreground">
                              Updated {formatDate(s.updated_at)}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                icon={Layers}
                title={`No ${SEGMENT_ENTITY_LABEL[t].toLowerCase()} segments yet`}
                description={
                  canManage
                    ? `Create a segment to capture a reusable filter for ${SEGMENT_ENTITY_LABEL[t].toLowerCase()} (e.g. "Active partners on the gold tier").`
                    : "You don't have permission to create segments. Ask an admin with the settings.write permission."
                }
                action={canManage ? <CreateSegmentSheet defaultEntityType={t} /> : undefined}
              />
            )}
          </TabsContent>
        );
      })}
    </Tabs>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="hidden text-right sm:block">
      <div className="font-display text-sm font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
    </div>
  );
}
